import React, { useContext, useRef, useEffect, useState } from "react";
import { observer } from "mobx-react";
import Loading from "../loading";
import { StoresContext } from "../../pages/_app";
import AnnotationTrackScrubber from "../annotation-track-scrubber";
import AnnotationRect from "../annotation-rect";
import { Annotation } from "../../api";
import { trackSegmentEvent } from "../../utils/analytics";
import { reaction } from "mobx";

const SCALE = 0.08;

interface Props {
  uuid: string;
  idx: number;
  assemblyUuid?: string;
}

const AnnotationTrack = (props: Props) => {
  const rootStore = useContext(StoresContext);
  const annotationStore = rootStore.annotationStore;

  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [currentScrollPosition, setCurrentScrollPosition] = useState<number>(0);

  useEffect(() => {
    const disposer = reaction(
      () => annotationStore.selectedUuid,
      (uuid) => {
        if (uuid && !annotationStore.jumpedOnTrack && containerRef.current) {
          const a = annotationStore.annotations.data.find(
            (a) => a.uuid === annotationStore.selectedUuid
          );
          if (a) {
            jumpToAnnotation(a);
            annotationStore.trackJumpDone();
          }
        }
      }
    );

    return () => {
      disposer();
    };
  }, []);

  if (annotationStore.loadingAnnotations) {
    return <Loading text="Loading..." />;
  }
  if (Object.keys(annotationStore.annotations.metadata).length == 0) {
    return null;
  }

  function jumpToPosition(offsetX: number, genomeLength: number) {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const trackWidth = container.getBoundingClientRect().width;
    const annotationLoc = (offsetX / trackWidth) * genomeLength;
    container.scrollLeft = annotationLoc * SCALE;
    setCurrentScrollPosition(container.scrollLeft);
  }

  function jumpToAnnotation(annotation: Annotation) {
    let geneName = "";
    if (annotation.gene && annotation.gene.name) {
      geneName = annotation.gene.name;
    }
    trackSegmentEvent("Jump to Annotation", {
      assemblyUuid: props.assemblyUuid,
      annotationUuid: annotation.uuid,
      geneName: geneName,
      context: "Annotations Viewer",
    });

    const container = containerRef.current;
    if (!container) {
      return;
    }

    // center the jumped-to annotation on the track
    const halfScreen = container.offsetWidth / 2;
    const halfAnnotation = (annotation.length / 2) * SCALE;
    container.scrollLeft = SCALE * annotation.absolute_start_loc - halfScreen + halfAnnotation;
    setCurrentScrollPosition(container.scrollLeft);
  }

  function renderContigPositions(contigPositions: number[]) {
    const positions = [];
    let runningSum = 0;
    for (let i = 0; i < contigPositions.length; i++) {
      runningSum += contigPositions[i];
      positions.push(
        <line
          x1={runningSum * SCALE}
          x2={runningSum * SCALE}
          y1={2}
          y2={16}
          stroke="#189493"
          strokeOpacity="0.7"
          strokeWidth="3"
          strokeLinecap="round"
          key={props.uuid + "-" + runningSum}
        />
      );
    }
    return positions;
  }

  const {
    annotations,
    selectedUuid,
    isSearching,
    isComparison,
    includeHypotheticalProteins,
    searchTerm,
  } = annotationStore;

  const metadata = annotations.metadata[props.uuid]!;
  const genomeLength = metadata.derived_metadata.length;
  const contigPositions = metadata.derived_metadata.contig_lengths;
  const genomeName = metadata.genome.name;
  const data = includeHypotheticalProteins
    ? annotations.data
    : annotationStore.withoutHypotheticalProteins;
  const results = searchTerm ? annotationStore.filteredAnnotations : [];

  return (
    <div
      className={
        isComparison ? "annotation-track--comparison annotation-track" : "annotation-track"
      }
    >
      {isComparison ? <div className="annotation-track__genome-label">{genomeName}</div> : null}
      <div className="annotation-track__container">
        <AnnotationTrackScrubber
          genomeLength={genomeLength}
          contigPositions={contigPositions}
          windowBeginsPercentage={((currentScrollPosition / genomeLength) * 100) / SCALE}
          jumpToPosition={jumpToPosition}
          jumpToAnnotation={jumpToAnnotation}
          searchResults={results}
        />
        <br />
        <div className="annotation-track__track" ref={containerRef}>
          <svg ref={svgRef} width={genomeLength * SCALE} height={20}>
            {data
              .filter((i) => i.annotation_set.uuid === props.uuid)
              .map((item) => {
                const obfuscated = isSearching && !results.find((a) => a.uuid === item.uuid);

                return (
                  <AnnotationRect
                    selected={selectedUuid === item.uuid}
                    annotation={item}
                    scale={SCALE}
                    key={item.uuid}
                    isObfuscated={obfuscated}
                    onClick={() => annotationStore.selectAnnotation(item)}
                    scrollLeftPosition={SCALE * item.absolute_start_loc}
                  />
                );
              })}
            {renderContigPositions(contigPositions)}
          </svg>
        </div>
      </div>
    </div>
  );
};

export default observer(AnnotationTrack);
